import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { getNameWithPercentageSplit } from "js/formatting";
import {
  getAllContingentBeneficiaryFromParties,
  getAllPrimaryBeneficiaryFromParties,
  isFlagEnabled,
} from "js/utilities";

import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";

export default function BeneficiariesCard({ data }) {
  const { t } = useTranslation();
  const beneficiaries = {
    primaryBeneficiaries: getAllPrimaryBeneficiaryFromParties(data.parties),
    contingents: getAllContingentBeneficiaryFromParties(data.parties),
  };
  const uuid = uuidv4();
  const [dialogOpen, setDialogOpen] = useState(false);
  useCollapsePanel(uuid);
  const isPolicyHasBeneficiary = () => {
    return (
      beneficiaries?.primaryBeneficiaries?.length > 0 ||
      beneficiaries?.contingents?.length > 0
    );
  };

  const [contactUsDialogState, setContactUsDialogState] = useState(false);

  return (
    <>
      <div className="ui-card mb-4 md:mb-6">
        <div className="md:flex md:flex-row md:items-start">
          <div className="px-4 pt-3 md:flex-grow xl:px-6 xl:pt-6">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label="Expand or collapse Beneficiary(s) details"
              >
                <span className="inline-block p-1 rounded-full mr-2 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span className="text-base font-body font-bold md:ui-no-badge-icon-inline">
                  {t("CustomerPolicyDetails-beneficiary-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("CustomerPolicyDetails-beneficiary-title")}
              </span>
            </h2>
          </div>
          <hr className="mb-0 mt-3 md:hidden" />
        </div>

        {isPolicyHasBeneficiary() && (
          <>
            <hr className="hidden md:block" />
            <div
              id={`${uuid}-panel`}
              className="px-4 pb-4 overflow-hidden h-20 mt-4 md:h-auto md:mt-0 xl:px-6 xl:pb-6"
            >
              {/* <!-- Beneficiary(s) Details --> */}
              <div className="grid grid-cols-2 gap-4 xl:gap-6">
                {beneficiaries?.primaryBeneficiaries?.length > 0 && (
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t(
                        "CustomerPolicyDetails-beneficiary-primary-beneficiarys"
                      )}
                    </div>
                    {beneficiaries?.primaryBeneficiaries?.map(
                      (primaryBeneficiary, idx) => {
                        return (
                          <Trans
                            i18nKey={getNameWithPercentageSplit(
                              primaryBeneficiary?.person,
                              primaryBeneficiary?.splitPercent,
                              primaryBeneficiary?.sharedEquallyFlag,
                              beneficiaries?.primaryBeneficiaries.length
                            )}
                            components={{
                              div1: (
                                <div className="text-sm text-theme-text-secondary font-semibold whitespace-nowrap" />
                              ),
                              div2: (
                                <span className="text-theme-text-tertiary" />
                              ),
                            }}
                          />
                        );
                      }
                    )}
                  </div>
                )}
                {beneficiaries?.contingents?.length > 0 && (
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t(
                        "CustomerPolicyDetails-beneficiary-contingent-beneficiarys"
                      )}
                    </div>
                    {beneficiaries?.contingents?.map((contingent, idx) => {
                      return (
                        <Trans
                          i18nKey={getNameWithPercentageSplit(
                            contingent?.person,
                            contingent?.splitPercent,
                            contingent?.sharedEquallyFlag,
                            beneficiaries?.contingents.length
                          )}
                          components={{
                            div1: (
                              <div className="text-sm text-theme-text-secondary font-semibold whitespace-nowrap" />
                            ),
                            div2: <span className="text-theme-text-tertiary" />,
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
              {/* <!-- Beneficiary(s) Details --> */}
            </div>
          </>
        )}
        <CardDisclaimerBase
          disclaimer={
            <Trans
              i18nKey={
                isPolicyHasBeneficiary()
                  ? "CustomerPolicyDetails-beneficiary-disclaimer"
                  : "CustomerPolicyDetails-no-beneficiary-disclaimer"
              }
              components={{
                button1: (
                  <button
                    className="ui-btn-hyperlink font-semibold"
                    onClick={() => setContactUsDialogState(true)}
                  />
                ),
              }}
            />
          }
        />
      </div>
      <GeneralPurposeDialog
        showCloseButton={true}
        enableSecondaryButton={false}
        dialogOnClose={() => null}
        state={dialogOpen}
        setState={setDialogOpen}
        title={t(
          "CustomerPolicyDetails-beneficiary-general-purpose-dialog-title"
        )}
        desc={
          <Trans
            i18nKey="CustomerPolicyDetails-beneficiary-general-purpose-dialog-desc"
            components={{
              link1: (
                <Link
                  className="font-semibold hover:underline text-ui-blue"
                  to="/services-claims/forms"
                ></Link>
              ),
            }}
          />
        }
      />
      <ContactUsDialog
        dialogState={contactUsDialogState}
        setDialogState={setContactUsDialogState}
      />
    </>
  );
}
